import React, { useMemo, useState } from 'react';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { ByMedia } from '@this/shared/ui/other/by_media';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import type { TripResponseArgs } from '@this/domain/trip/trip2';
import type { ChatMessage } from '../types/messages';
import type { UserInfo } from '../types/user';
import type { ProposalNotification } from '../hooks/useAiMessages';
import { useChatInput } from '../hooks/useChatInput';
import { useConversationState } from '../hooks/useConversationState';
import type { ProposalBundle } from '../types/proposalBundles';
import type { ReservationInfo } from '../api/reservationInfo';
import type { FormValues } from '../types/reservationInfoForm';
import SidebarMenu from './SidebarMenu';
import ChatArea from './ChatArea';
import InfoPanel from './InfoPanel';
import RulesPanel from './RulesPanel';
import SessionWarningModal from './SessionWarningModal';
import HistoryListPanel from './HistoryListPanel';
import UserChatRuleSettingsModal from './UserChatRuleSettingsModal';

interface Props {
  tripId: string;
  trip: TripResponseArgs | null;
  user?: UserInfo;
  pusherKey: string;
  pusherChannelPrefix: string;
  headerTitle?: string;
  subtitle?: string;
  children?: React.ReactNode;
  enableMessaging?: boolean;
  initialMessages?: ChatMessage[];
  onProposalReceived?: (notification: ProposalNotification) => void;
  onReservationFormReceived: () => void;
  proposalBundles?: ProposalBundle[];
  onSelectItem?: (bundleId: number, itemId: number) => void;
  onCloseItem?: (bundleId: number) => void;
  reservationInfo: ReservationInfo | null;
  onSubmitReservationInfo?: (values: FormValues) => Promise<boolean>;
  isSubmittingReservationInfo?: boolean;
  onArrangementRequestSuccess?: () => Promise<void>;
  onReservationInfoFormEditableChange: (editable: boolean) => void;
  isReservationInfoFormEditable: boolean;
  onTripUpdated?: () => void;
}

const TripAiMessagesContainer = ({
  tripId,
  trip,
  user,
  pusherKey,
  pusherChannelPrefix,
  headerTitle,
  subtitle,
  children,
  enableMessaging,
  initialMessages = [],
  onProposalReceived,
  onReservationFormReceived,
  proposalBundles = [],
  onSelectItem,
  onCloseItem,
  reservationInfo,
  onSubmitReservationInfo,
  isSubmittingReservationInfo,
  onArrangementRequestSuccess,
  onReservationInfoFormEditableChange,
  isReservationInfoFormEditable,
  onTripUpdated
}: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const isTripLoading = trip === null;
  const [showHistoryList, setShowHistoryList] = useState(false);
  const [showUserChatRuleSettings, setShowUserChatRuleSettings] = useState(false);
  const [isSidebarDrawerOpen, setIsSidebarDrawerOpen] = useState(false);
  const [isInfoDrawerOpen, setIsInfoDrawerOpen] = useState(false);
  const [isRulesOpen, setIsRulesOpen] = useState(false);

  const chatInput = useChatInput();
  const conversationState = useConversationState(tripId);
  const [isArrangementRequested, setIsArrangementRequested] = useState(false);

  const proposalCount = proposalBundles.length;

  const closeDrawers = () => {
    setIsSidebarDrawerOpen(false);
    setIsInfoDrawerOpen(false);
  };

  const handleShowHistoryList = () => {
    setIsSidebarDrawerOpen(false);
    setShowHistoryList(true);
  };

  const handleHideHistoryList = () => {
    setShowHistoryList(false);
  };

  const sidebarMenu = (
    <SidebarMenu
      currentTripId={tripId}
      onShowAllHistory={handleShowHistoryList}
      onOpenUserChatRuleSettings={() => setShowUserChatRuleSettings(true)}
      onNavigate={closeDrawers}
    />
  );

  const renderChatArea = (isSpLayout: boolean) => (
    <ChatArea
      tripId={tripId}
      trip={trip}
      user={user}
      pusherKey={pusherKey}
      pusherChannelPrefix={pusherChannelPrefix}
      headerTitle={headerTitle}
      subtitle={subtitle}
      enableMessaging={!isTripLoading && enableMessaging !== false}
      initialMessages={initialMessages}
      onProposalReceived={onProposalReceived}
      onReservationFormReceived={onReservationFormReceived}
      proposalBundles={proposalBundles}
      onSelectItem={onSelectItem}
      onCloseItem={onCloseItem}
      reservationInfo={reservationInfo}
      onSubmitReservationInfo={onSubmitReservationInfo}
      isSubmittingReservationInfo={isSubmittingReservationInfo}
      onArrangementRequestSuccess={onArrangementRequestSuccess}
      onReservationInfoFormEditableChange={onReservationInfoFormEditableChange}
      inputValue={chatInput.inputValue}
      onInputChange={chatInput.handleInputChange}
      appendToInput={chatInput.appendToInput}
      resetInput={chatInput.resetInput}
      textareaRef={chatInput.textareaRef}
      blockers={conversationState.blockers}
      searchTargets={conversationState.searchTargets}
      onConversationStateChanged={conversationState.refetch}
      onArrangementRequestedChange={setIsArrangementRequested}
      isSpLayout={isSpLayout}
      onOpenSidebarDrawer={() => setIsSidebarDrawerOpen(true)}
      onOpenInfoDrawer={trip ? () => setIsInfoDrawerOpen(true) : undefined}
      proposalCount={proposalCount}
    >
      {children}
    </ChatArea>
  );

  const renderInfoPanel = (isSpLayout: boolean) =>
    trip ? (
      <InfoPanel
        tripId={tripId}
        trip={trip}
        proposalBundles={proposalBundles}
        isEditDisabled={!isReservationInfoFormEditable}
        reservationInfo={reservationInfo}
        onTripUpdated={onTripUpdated}
        inputSupport={{
          blockers: conversationState.blockers,
          decidedFields: conversationState.decidedFields,
          searchTargets: conversationState.searchTargets,
          readyToSearch: conversationState.readyToSearch,
          isLoading: conversationState.isLoading,
          isArrangementRequested,
          hasConversationState: conversationState.hasConversationState,
          onChipClick: chatInput.appendToInput,
          onBulkInsert: chatInput.appendToInput
        }}
        fullWidth={isSpLayout}
        onOpenTravelRules={isSpLayout ? () => setIsRulesOpen(true) : undefined}
      />
    ) : (
      <PlaceholderPanel />
    );

  return (
    <div style={styles.root}>
      <div style={styles.body}>
        <ByMedia>
          {matches =>
            matches.sm || matches.md ? (
              <>
                {showHistoryList ? <HistoryListPanel onBack={handleHideHistoryList} /> : renderChatArea(true)}
                {(isSidebarDrawerOpen || isInfoDrawerOpen) && (
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label="閉じる"
                    style={styles.drawerOverlay}
                    onClick={closeDrawers}
                    onKeyDown={e => e.key === 'Escape' && closeDrawers()}
                  />
                )}
                <div
                  style={{
                    ...styles.leftDrawer,
                    transform: isSidebarDrawerOpen ? 'translateX(0)' : 'translateX(-100%)'
                  }}
                >
                  <div style={styles.drawerCloseRow}>
                    <button
                      type="button"
                      style={styles.drawerCloseButton}
                      onClick={() => setIsSidebarDrawerOpen(false)}
                      aria-label="閉じる"
                    >
                      ✕
                    </button>
                  </div>
                  <div style={styles.drawerScroll}>{sidebarMenu}</div>
                </div>
                {trip && (
                  <div
                    style={{
                      ...styles.rightDrawer,
                      transform: isInfoDrawerOpen ? 'translateX(0)' : 'translateX(100%)'
                    }}
                  >
                    <div style={styles.drawerCloseRow}>
                      <button
                        type="button"
                        style={styles.drawerCloseButton}
                        onClick={() => setIsInfoDrawerOpen(false)}
                        aria-label="閉じる"
                      >
                        ✕
                      </button>
                    </div>
                    <div style={styles.drawerScroll}>{renderInfoPanel(true)}</div>
                  </div>
                )}
                <RulesPanel
                  isSpLayout
                  open={isRulesOpen}
                  onOpen={() => setIsRulesOpen(true)}
                  onClose={() => setIsRulesOpen(false)}
                />
              </>
            ) : (
              <>
                {sidebarMenu}
                {showHistoryList ? (
                  <HistoryListPanel onBack={handleHideHistoryList} />
                ) : (
                  <>
                    {renderChatArea(false)}
                    {renderInfoPanel(false)}
                  </>
                )}
                <RulesPanel
                  isSpLayout={false}
                  open={isRulesOpen}
                  onOpen={() => setIsRulesOpen(true)}
                  onClose={() => setIsRulesOpen(false)}
                />
              </>
            )
          }
        </ByMedia>
        <SessionWarningModal />
        <UserChatRuleSettingsModal
          open={showUserChatRuleSettings}
          onClose={() => setShowUserChatRuleSettings(false)}
        />
      </div>
    </div>
  );
};

const getStyles = (theme: AITTheme) => ({
  root: {
    width: '100%',
    height: 'calc(100vh - 80px)',
    display: 'flex' as const,
    flexDirection: 'column' as const,
    minHeight: 0
  },
  body: {
    display: 'flex' as const,
    flex: 1,
    position: 'relative' as const,
    backgroundColor: theme.whiteColor,
    border: `1px solid ${theme.grayBorderColor}`,
    borderRadius: '8px',
    overflow: 'hidden',
    minHeight: 0
  },
  drawerOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000
  },
  leftDrawer: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    width: 'min(80vw, 280px)',
    height: '100vh',
    backgroundColor: theme.contentBgColor,
    boxShadow: '2px 0 8px rgba(0, 0, 0, 0.15)',
    zIndex: 1001,
    transition: 'transform 0.3s ease-in-out',
    display: 'flex' as const,
    flexDirection: 'column' as const,
    overflow: 'hidden' as const
  },
  rightDrawer: {
    position: 'fixed' as const,
    top: 0,
    right: 0,
    width: 'min(88vw, 340px)',
    height: '100vh',
    backgroundColor: '#e6f4fb',
    boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.15)',
    zIndex: 1001,
    transition: 'transform 0.3s ease-in-out',
    display: 'flex' as const,
    flexDirection: 'column' as const,
    overflow: 'hidden' as const
  },
  drawerCloseRow: {
    display: 'flex' as const,
    justifyContent: 'flex-end' as const,
    padding: '8px',
    flexShrink: 0
  },
  drawerCloseButton: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    lineHeight: 1,
    color: theme.textColor,
    cursor: 'pointer',
    padding: '4px 8px'
  },
  drawerScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto' as const,
    display: 'flex' as const,
    flexDirection: 'column' as const
  }
});

const PlaceholderPanel = () => {
  const { theme } = useAITTheme();
  const styles = useMemo(
    () => ({
      width: '320px',
      padding: '20px',
      backgroundColor: theme.contentBgColor,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }),
    [theme]
  );
  return (
    <div style={styles}>
      <Loading />
    </div>
  );
};

export default TripAiMessagesContainer;
